import dayjs from 'dayjs';
import 'dayjs/locale/th';

dayjs.locale('th');

const initializePdfMake = () => {
  if (!window.pdfMake) throw new Error('ไม่พบระบบสร้าง PDF');
  window.pdfMake.fonts = {
    THSarabunNew: {
      normal: 'THSarabunNew.ttf',
      bold: 'THSarabunNew Bold.ttf',
      italics: 'THSarabunNew Italic.ttf',
      bolditalics: 'THSarabunNew BoldItalic.ttf'
    }
  };
};

const number = value => Number(value || 0).toLocaleString('th-TH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const signedAmount = (row, field) => {
  const value = Number(row[field] || 0);
  return row.type === 'expense' ? -value : value;
};

const accountRow = row => ([
  { text: row.name || '-', margin: [14, 1, 0, 1] },
  { text: row.code || '-', alignment: 'center', margin: [0, 1, 0, 1] },
  { text: number(signedAmount(row, 'period_amount')), alignment: 'right', margin: [0, 1, 0, 1] },
  { text: number(signedAmount(row, 'cumulative_amount')), alignment: 'right', margin: [0, 1, 0, 1] }
]);

const totalAmountCell = value => ({
  stack: [
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 84, y2: 0, lineWidth: 0.8 }] },
    { text: number(value), bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 84, y2: 0, lineWidth: 0.8 }] }
  ],
  margin: [0, 2, 0, 2]
});

const totalRow = (label, periodValue, cumulativeValue) => ([
  { text: label, colSpan: 2, bold: true, alignment: 'right', margin: [0, 7, 8, 2] }, {},
  totalAmountCell(periodValue),
  totalAmountCell(cumulativeValue)
]);

const createTableBody = (rows, summary) => {
  const incomeRows = rows.filter(row => row.type === 'income');
  const expenseRows = rows.filter(row => row.type === 'expense');
  return [
    [
      { text: 'บัญชี', style: 'tableHeader' },
      { text: 'รหัสบัญชี', style: 'tableHeader', alignment: 'center' },
      { text: 'ประจำงวด', style: 'tableHeader', alignment: 'right' },
      { text: 'จนถึงงวดนี้', style: 'tableHeader', alignment: 'right' }
    ],
    [{ text: 'รายได้', colSpan: 4, bold: true, margin: [0, 7, 0, 3] }, {}, {}, {}],
    ...incomeRows.map(accountRow),
    totalRow('รวมรายได้ทั้งสิ้น', summary.revenue, summary.cumulative_revenue),
    [{ text: '', colSpan: 4, margin: [0, 7, 0, 0] }, {}, {}, {}],
    [{ text: 'ค่าใช้จ่าย', colSpan: 4, bold: true, margin: [0, 7, 0, 3] }, {}, {}, {}],
    ...expenseRows.map(accountRow),
    totalRow('รวมค่าใช้จ่ายทั้งสิ้น', summary.expenses, summary.cumulative_expenses),
    [{ text: '', colSpan: 4, margin: [0, 7, 0, 0] }, {}, {}, {}],
    totalRow('กำไร (ขาดทุน)', summary.net_profit, summary.cumulative_net_profit)
  ];
};

export const createIncomeStatementDefinition = ({ rows, summary, company, period }) => {
  const companyName = company?.company_name || company?.name || 'ชื่อกิจการ';
  const endDate = dayjs(period[1]);
  const buddhistYear = endDate.year() + 543;
  const periodLabel = period[0].isSame(period[1], 'month')
    ? `สำหรับงวด สิ้นสุดวันที่ ${endDate.format('D MMMM')} ${buddhistYear}`
    : `สำหรับงวด ตั้งแต่วันที่ ${dayjs(period[0]).format('D MMMM')} ถึงวันที่ ${endDate.format('D MMMM')} ${buddhistYear}`;

  return {
    pageSize: 'A4',
    pageMargins: [42, 38, 42, 45],
    defaultStyle: { font: 'THSarabunNew', fontSize: 13, color: '#111111' },
    footer: (currentPage, pageCount) => ({
      text: `หน้า ${currentPage}/${pageCount}`,
      alignment: 'right',
      margin: [0, 8, 42, 0],
      fontSize: 10
    }),
    content: [
      { text: companyName, alignment: 'center', bold: true, fontSize: 18, margin: [0, 0, 0, 0] },
      { text: 'งบกำไรขาดทุน', alignment: 'center', bold: true, fontSize: 16 },
      { text: 'ประจำงวด', alignment: 'center', bold: true, fontSize: 13 },
      { text: periodLabel, alignment: 'center', bold: true, fontSize: 13, margin: [0, 0, 0, 10] },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 95, 90, 90],
          body: createTableBody(rows, summary)
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 6,
          paddingTop: () => 1,
          paddingBottom: () => 1
        }
      },
      { text: 'ขอรับรองว่า รายงานนี้ได้จัดทำโดยถูกต้อง', margin: [0, 34, 0, 0], fontSize: 12 }
    ],
    styles: {
      tableHeader: { bold: true, fontSize: 13 }
    }
  };
};

export const generateIncomeStatementPdf = payload => {
  initializePdfMake();
  const generator = window.pdfMake.createPdf(createIncomeStatementDefinition(payload));
  return new Promise((resolve, reject) => {
    try {
      generator.getDataUrl(resolve);
    } catch (error) {
      reject(error);
    }
  });
};
